import { Button, Modal } from "react-bootstrap";
import { KTSVG } from "../../../helpers";
import { useEffect, useState, useCallback, useMemo } from "react";
import axios, { AxiosRequestHeaders } from "axios";
import { getAuth } from "../../../../app/modules/auth";
import { TablesWidget3, userListInterface, userInterface } from "../../widgets";
import Collapse from "react-bootstrap/Collapse";
import jsPDF from "jspdf";
import "jspdf-autotable";

declare module "jspdf" {
  interface jsPDF {
    // Declara o método autoTable e sua assinatura básica
    autoTable: (options: any) => jsPDF;
  }
}

type usersListByCategoryProps = {
  showModal: boolean;
  selectedItemModal: string;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  handleClose: () => void;
};

export interface ColumnOption {
  id:
    | keyof userInterface
    | "profile_image"
    | "_id"
    | "full_name"
    | "contract_period"
    | "gender"
    | "status_badge"
    | "Endereco";
  label: string;
  defaultVisible: boolean;
}

const initialColumnOptions: ColumnOption[] = [
  { id: "_id", label: "Código", defaultVisible: true },
  { id: "profile_image", label: "Foto", defaultVisible: true },
  { id: "full_name", label: "Nome", defaultVisible: true },
  { id: "cpf", label: "CPF", defaultVisible: true },
  { id: "birthdate", label: "Data Nascimento", defaultVisible: false },
  { id: "gender", label: "Gênero", defaultVisible: true },
  { id: "celphone", label: "Telefone", defaultVisible: true },
  { id: "email", label: "E-mail", defaultVisible: false },
  { id: "instagram", label: "Instagram", defaultVisible: true },
  { id: "status_badge", label: "Status", defaultVisible: true },
];

const mockUsers: userInterface[] = [
  {
    _id: 101,
    full_name: "",
    cpf: "",
    birthdate: "",
    gender: "",
    celphone: "",
    email: "",
    instagram: "",
    first_name: "",
    last_name: "",
    banned: false,
    category: "",
    profile_image: "",
    user_status: 1,
    compliance: true,
    overdue: false,
    addresses: [], // Adicionado para evitar erro no CsvDownloadButton
  } as userInterface,
];

const mockUserList: userListInterface = {
  users: mockUsers,
  category: "registeredUsers",
} as userListInterface;

export interface CategoryFilterOption {
  id: string;
  label: string;
  defaultChecked: boolean;
}

const checkboxCategoryOptions: CategoryFilterOption[] = [
  {
    id: "complianceUsers",
    label: "Usuários adimplentes",
    defaultChecked: false,
  },
  {
    id: "nonComplianceUsers",
    label: "Usuários inadimplentes",
    defaultChecked: false,
  },
  { id: "bannedUsers", label: "Usuários banidos", defaultChecked: false },
  { id: "visitorsOnline", label: "Visitantes", defaultChecked: false },
  { id: "members", label: "Sócios", defaultChecked: false },
  { id: "activeUsers", label: "Ativos", defaultChecked: false },
  { id: "inactiveUsers", label: "Inativos", defaultChecked: false },
  { id: "athletes", label: "Atletas", defaultChecked: false },
];

const initialCategoryMapping: { [key: string]: string } = {
  complianceUsers: "complianceUsers",
  nonComplianceUsers: "nonComplianceUsers",
  bannedUsers: "bannedUsers",
  registeredUsers: "activeUsers",
  usersOnline: "activeUsers",
  allUsersOnline: "activeUsers",
  visitorsOnline: "visitorsOnline",
};

const genderOptions = [
  { value: "male", label: "Masculino" },
  { value: "female", label: "Feminino" },
];

const categoriesMap: { [key: string]: string } = {
  registeredUsers: "Usuários registrados",
  complianceUsers: "Sócios em dia",
  nonComplianceUsers: "Sócios em Atraso",
  bannedUsers: "Banidos",
  usersOnline: "Usuários online",
  visitorsOnline: "Visitantes online",
  allUsersOnline: "Total de Usuários Online",
};

const UserListByCategoryModal: React.FC<usersListByCategoryProps> = ({
  showModal,
  selectedItemModal,
  setShowModal,
  handleClose,
}) => {
  const [userList, setUserList] = useState<userListInterface>();
  const apiUrl = process.env.REACT_APP_API_URL;
  const apiToken = getAuth();
  const token = apiToken?.api_token;
  const module = `topbar/report`;

  const [visibleColumns, setVisibleColumns] =
    useState<ColumnOption[]>(initialColumnOptions);
  const [selectedPlanId, setSelectedPlanId] = useState<string>("");
  const [selectedGender, setSelectedGender] = useState<string>("all");
  const [contractPeriod, setContractPeriod] = useState<{
    start: string;
    end: string;
  }>({ start: "", end: "" });
  const [planOptions] = useState<{ id: string; name: string }[]>([]);

  const [isPersonalized, setIsPersonalized] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<
    CategoryFilterOption[]
  >(checkboxCategoryOptions.map((opt) => ({ ...opt, defaultChecked: false })));

  const [openCollapse, setOpenCollapse] = useState(false);

  useEffect(() => {
    const targetId = initialCategoryMapping[selectedItemModal];
    setSelectedCategories(
      checkboxCategoryOptions.map((opt) => ({
        ...opt,
        defaultChecked: opt.id === targetId,
      })),
    );
    setIsPersonalized(false);
    setSelectedPlanId("");
    setSelectedGender("all");
    setContractPeriod({ start: "", end: "" });
  }, [selectedItemModal]);

  const handleCategoryChange = useCallback(
    (categoryId: string, isChecked: boolean) => {
      setIsPersonalized(true);
      setSelectedCategories((prev) =>
        prev.map((cat) =>
          cat.id === categoryId ? { ...cat, defaultChecked: isChecked } : cat,
        ),
      );
    },
    [],
  );

  const handleColumnChange = useCallback(
    (columnId: string, isChecked: boolean) => {
      setIsPersonalized(true);
      setVisibleColumns((prev) =>
        prev.map((col) =>
          col.id === columnId ? { ...col, defaultVisible: isChecked } : col,
        ),
      );
    },
    [],
  );

  const handleContractPeriodChange = useCallback(
    (key: "start" | "end", value: string) => {
      setIsPersonalized(true);
      setContractPeriod((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const modalTitle = useMemo(() => {
    if (isPersonalized) {
      return "Filtro de Usuários Personalizado";
    }
    return categoriesMap[selectedItemModal] || "Filtro de Usuários";
  }, [isPersonalized, selectedItemModal]);

  const findHeaders: AxiosRequestHeaders = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
      operation: `${module}/find`,
    }),
    [token, module],
  );

  const activeCategories = useMemo(() => {
    return selectedCategories
      .filter((cat) => cat.defaultChecked)
      .map((cat) => cat.id)
      .join(",");
  }, [selectedCategories]);

  const columnsToDisplay = useMemo(() => {
    return visibleColumns.filter((col) => col.defaultVisible);
  }, [visibleColumns]);

  const activeFilters = useMemo(
    () => ({
      initialCategory: initialCategoryMapping[selectedItemModal],
      activeCategories: activeCategories,
      selectedPlanId: selectedPlanId,
      selectedGender: selectedGender,
      contractStart: contractPeriod.start,
      contractEnd: contractPeriod.end,
      isPersonalized: isPersonalized,
    }),
    [
      selectedItemModal,
      activeCategories,
      selectedPlanId,
      selectedGender,
      contractPeriod,
      isPersonalized,
    ],
  );

  const [filtersActive, setFiltersActive] = useState(false);

  const loadData = useCallback(
    async (
      module: string,
      headers: AxiosRequestHeaders,
      filters: typeof activeFilters,
    ) => {
      const params = new URLSearchParams();
      setFiltersActive(false);
      let isInitialLoad = !filters.isPersonalized;

      if (!filters.isPersonalized && filters.initialCategory) {
        params.append("categories", filters.initialCategory);
        setFiltersActive(true);
      }

      if (filters.isPersonalized && filters.activeCategories) {
        params.append("categories", filters.activeCategories);
        setFiltersActive(true);
      }

      if (filters.selectedPlanId) {
        params.append("planId", filters.selectedPlanId);
        setFiltersActive(true);
      }
      if (filters.selectedGender !== "all") {
        params.append("gender", filters.selectedGender);
        setFiltersActive(true);
      }

      if (filters.contractStart && filters.contractEnd) {
        params.append("contractStart", filters.contractStart);
        params.append("contractEnd", filters.contractEnd);
        setFiltersActive(true);
      }

      const queryString = params.toString();

      let apiModule = module;
      if (filters.isPersonalized || filtersActive) {
        apiModule = "topbar/report/advanced";
      }

      if (isInitialLoad && filters.initialCategory === "registeredUsers") {
        setUserList(mockUserList);
        return;
      }

      if (!filtersActive) {
        setUserList(undefined);
        return;
      }

      try {
        const response = await axios.get(
          `${apiUrl}/${apiModule}?${queryString}`,
          { headers },
        );
        const data = response.data;

        if (data && data.users) {
          data.users = data.users.map((user: any) => {
            if (user.birthdate) {
              try {
                const date = new Date(user.birthdate);
                const day = String(date.getUTCDate()).padStart(2, "0");
                const month = String(date.getUTCMonth() + 1).padStart(2, "0");
                const year = date.getUTCFullYear();

                user.birthdate = `${day}/${month}/${year}`;
              } catch (e) {
                console.error("Falha ao formatar data na tela:", e);
              }
            }

            if (!user.full_name) {
              user.full_name = `${user.first_name || ""} ${
                user.last_name || ""
              }`.trim();
            }

            return user;
          });
        }
        setUserList(response.data);
      } catch (error) {
        console.error("Erro na busca de usuários:", error);
        setUserList(undefined);
      }
    },
    [apiUrl, setUserList, filtersActive],
  );

  useEffect(() => {
    if (!module || !showModal) return;
    loadData(module, findHeaders, activeFilters);
  }, [
    module,
    showModal,
    loadData,
    findHeaders,
    planOptions.length,
    activeFilters,
  ]);

  const [showPrintConfig, setShowPrintConfig] = useState(false);
  const [printSettings, setPrintSettings] = useState({
    orientation: "l" as "p" | "l",
    pageSize: "a4",
  });

  const getExportQueryString = (filters: typeof activeFilters): string => {
    const params = new URLSearchParams();

    if (filters.isPersonalized && filters.activeCategories) {
      params.append("categories", filters.activeCategories);
    }

    return params.toString();
  };

  const ExportKeyMap: { [key: string]: string } = {
    _id: "Código",
    full_name: "Nome",
    cpf: "CPF",
    birthdate: "Data Nascimento",
    gender: "Gênero",
    celphone: "Celular",
    email: "Email",
    instagram: "Instagram",
    status_badge: "Status",
  };

  const getBase64ImageFromURL = (url: string): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const cacheBusterUrl = url.includes("?")
        ? `${url}&t=${Date.now()}`
        : `${url}?t=${Date.now()}`;

      img.setAttribute("crossOrigin", "anonymous");

      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        ctx?.drawImage(img, 0, 0);
        try {
          const dataURL = canvas.toDataURL("image/png");
          resolve(dataURL);
        } catch (e) {
          reject(new Error("Erro ao converter canvas para DataURL"));
        }
      };

      img.onerror = (error) => reject(error);
      img.src = cacheBusterUrl;
    });
  };

  const handleExport = async (format: "pdf" | "excel") => {
    const queryString = getExportQueryString(activeFilters);
    let apiModule = "";
    let fileName = "";

    if (format === "pdf") {
      apiModule = "topbar/report/printpdf";
      fileName = `relatorio_usuarios_${new Date().toISOString()}.pdf`;
      try {
        const response = await axios.get(
          `${apiUrl}/${apiModule}?${queryString}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            responseType: "json",
          },
        );

        const dataFromApi = response.data;
        const usersToExport = dataFromApi?.users;
        const selectedColumns = columnsToDisplay;
        const companyInfo = dataFromApi.company;

        const logoUrl = `https://companies-admin.aacceson.com/${companyInfo.logo}`;

        let base64Logo: string = "";
        try {
          base64Logo = await getBase64ImageFromURL(logoUrl);
        } catch (e) {
          console.error(
            "Erro ao processar logo para o PDF, usando fallback",
            e,
          );
          base64Logo = window.location.origin + "/media/logos/Logo1992.png";
        }

        if (!usersToExport || usersToExport.length === 0) {
          alert(
            "Nenhum usuário encontrado no retorno da API para gerar o PDF.",
          );
          return;
        }

        const formattedData = usersToExport.map((user: any) => {
          return selectedColumns.map((col) => {
            switch (col.id) {
              case "_id":
                return String(user._id || "");
              case "full_name":
                return `${user.first_name || ""} ${user.last_name || ""}`;
              case "birthdate":
                if (!user.birthdate) return "";

                try {
                  const date = new Date(user.birthdate);
                  const day = String(date.getUTCDate()).padStart(2, "0");
                  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
                  const year = date.getUTCFullYear();

                  return `${day}/${month}/${year}`;
                } catch (e) {
                  return user.birthdate;
                }
              case "status_badge":
                return String(user.status || "");
              default:
                return String(user[col.id] || "");
            }
          });
        });

        const doc = new jsPDF({
          orientation: printSettings.orientation,
          unit: "mm",
          format: printSettings.pageSize,
        });

        const pageWidth = doc.internal.pageSize.getWidth();
        const margin = 10;

        const imgWidth = 15;
        const imgHeight = 10;
        const yPos = 7;

        if (base64Logo) {
          doc.addImage(base64Logo, "PNG", margin, yPos, imgWidth, imgHeight);
        }

        const xRight = pageWidth - margin - imgWidth;
        if (base64Logo) {
          doc.addImage(base64Logo, "PNG", xRight, yPos, imgWidth, imgHeight);
        }

        doc.setFontSize(10);
        doc.setTextColor(0, 0, 0);

        const centerX = pageWidth / 2;
        const lineSpacing = 5;

        const line1 = companyInfo?.name?.toUpperCase() || "NOME DA EMPRESA";
        const line2 = companyInfo?.locais?.[0]?.endereco || "";
        const line3 = companyInfo?.locais?.[0]?.telefone || "";

        doc.setFontSize(8);
        doc.text(line1, centerX, yPos + 3, { align: "center" });
        doc.text(line2, centerX, yPos + 3 + lineSpacing, { align: "center" });
        doc.text(line3, centerX, yPos + 3 + lineSpacing * 2, {
          align: "center",
        });

        let startY = yPos + lineSpacing * 3;

        const tableWidth = pageWidth - margin * 2;
        const colWidth = tableWidth / selectedColumns.length;

        const textColor = 50;

        doc.setFillColor(52, 73, 94);
        doc.rect(margin, startY, tableWidth, 8, "F");
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(9);

        doc.rect(margin, startY, tableWidth, 8, "F");
        let currentX = margin;
        selectedColumns.forEach((col) => {
          doc.text(col.label, currentX + 2, startY + 5);
          currentX += colWidth;
        });

        doc.setTextColor(textColor);
        doc.setFontSize(8);
        startY += 8;

        formattedData.forEach((row: string[]) => {
          if (startY > doc.internal.pageSize.getHeight() - 20) {
            doc.addPage();
            startY = 20;
          }
          currentX = margin;
          row.forEach((cellText) => {
            doc.text(cellText, currentX + 2, startY + 5);
            currentX += colWidth;
          });
          startY += 7;
        });

        doc.save(fileName);
        setShowPrintConfig(false);
      } catch (error) {
        console.error(`Erro ao exportar para ${format}:`, error);
      }
    } else {
      apiModule = "topbar/report/exportexcel";
      fileName = `relatorio_usuarios_${new Date().toISOString()}.csv`; // 🛑 MUDANÇA PARA XLSX
      try {
        const response = await axios.get(
          `${apiUrl}/${apiModule}?${queryString}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            responseType: "json",
          },
        );

        const dataFromApi = response.data;
        const usersToExport = dataFromApi?.users;

        if (!usersToExport || usersToExport.length === 0) {
          alert("Nenhum usuário encontrado no retorno da API para exportação.");
          return;
        }

        const selectedColumns = columnsToDisplay;
        const formattedData: any[] = usersToExport.map((user: any) => {
          const row: any = {};
          const isUserVisitor = user?.accessBracelet?.braceletColor?.visitors;
          const categoryKey = user?.category
            ? user?.category
            : isUserVisitor
            ? "visitorsOnline"
            : dataFromApi?.category;
          const status = categoryKey;

          let formattedAddress = "";
          const addressColumnIsSelected = selectedColumns.some(
            (col) => col.id === "Endereco",
          );

          if (addressColumnIsSelected) {
            const addressFragments = [
              user.addresses?.[0]?.address,
              user.addresses?.[0]?.number,
              user.addresses?.[0]?.neighborhood,
              user.addresses?.[0]?.city,
              user.addresses?.[0]?.birthdate,
              user.addresses?.[0]?.state,
              user.addresses?.[0]?.country,
            ];
            formattedAddress = addressFragments
              .filter((fragment) => fragment !== undefined && fragment !== null)
              .join(", ");
          }

          selectedColumns.forEach((col) => {
            const exportLabel =
              ExportKeyMap[col.id as keyof typeof ExportKeyMap] || col.label;

            switch (col.id) {
              case "_id":
                row[exportLabel] = user._id;
                break;
              case "full_name":
                row[exportLabel] = `${user.first_name || ""} ${
                  user.last_name || ""
                }`;
                break;
              case "status_badge":
                row[exportLabel] = status;
                break;
              case "profile_image":
                row[exportLabel] = user.profile_image ? "Sim" : "Não";
                break;
              case "cpf":
                row[exportLabel] = user.cpf;
                break;
              case "celphone":
                row[exportLabel] = user.celphone;
                break;
              case "email":
                row[exportLabel] = user.email;
                break;
              case "instagram":
                row[exportLabel] = user.instagram;
                break;
              case "birthdate":
                if (user.birthdate) {
                  try {
                    const date = new Date(user.birthdate);
                    const day = String(date.getDate()).padStart(2, "0");
                    const month = String(date.getMonth() + 1).padStart(2, "0");
                    const year = String(date.getFullYear()).slice(-2);
                    row[exportLabel] = `${day}/${month}/${year}`;
                  } catch (error) {
                    console.error(
                      "Erro ao formatar data:",
                      user.birthdate,
                      error,
                    );
                    row[exportLabel] = user.birthdate;
                  }
                } else {
                  row[exportLabel] = "";
                }
                break;
              case "gender":
                row[exportLabel] = user[col.id] || "";
                break;
              case "Endereco":
                row[exportLabel] = formattedAddress;
                break;
            }
          });

          return row;
        });
        const headers =
          formattedData.length > 0
            ? Object.keys(formattedData[0]).join(";")
            : "";
        const csvRows = formattedData.map((d: any) =>
          Object.values(d)
            .map((value) => `"${String(value).replace(/"/g, '""')}"`)
            .join(";"),
        );

        const CSV = [headers, ...csvRows].join("\n");
        const BOM = "\uFEFF";
        const blob = new Blob([BOM + CSV], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error(`Erro ao exportar para ${format}:`, error);
      }
    }
  };

  const handlePrint = async () => {
    const queryString = getExportQueryString(activeFilters);
    const apiModule = "topbar/report/printpdf";

    if (!userList) return;

    try {
      const response = await axios.get(
        `${apiUrl}/${apiModule}?${queryString}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "json",
        },
      );

      const dataFromApi = response.data;
      const usersToExport = dataFromApi?.users;
      const selectedColumns = columnsToDisplay;
      const companyInfo = dataFromApi.company;

      const logoUrl = `https://companies-admin.aacceson.com/${companyInfo.logo}`;

      let base64Logo: string = "";
      try {
        base64Logo = await getBase64ImageFromURL(logoUrl);
      } catch (e) {
        console.error("Erro ao processar logo para o PDF, usando fallback", e);
        base64Logo = window.location.origin + "/media/logos/Logo1992.png";
      }

      if (!usersToExport || usersToExport.length === 0) {
        alert("Nenhum usuário encontrado no retorno da API para gerar o PDF.");
        return;
      }

      const formattedData = usersToExport.map((user: any) => {
        return selectedColumns.map((col) => {
          switch (col.id) {
            case "_id":
              return String(user._id || "");
            case "full_name":
              return `${user.first_name || ""} ${user.last_name || ""}`;
            case "birthdate":
              if (!user.birthdate) return "";

              try {
                const date = new Date(user.birthdate);
                const day = String(date.getUTCDate()).padStart(2, "0");
                const month = String(date.getUTCMonth() + 1).padStart(2, "0");
                const year = date.getUTCFullYear();

                return `${day}/${month}/${year}`;
              } catch (e) {
                return user.birthdate;
              }
            case "status_badge":
              return String(user.status || "");
            default:
              return String(user[col.id] || "");
          }
        });
      });

      const doc = new jsPDF({
        orientation: printSettings.orientation,
        unit: "mm",
        format: printSettings.pageSize,
      });

      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 10;

      const imgWidth = 15;
      const imgHeight = 10;
      const yPos = 7;

      if (base64Logo) {
        doc.addImage(base64Logo, "PNG", margin, yPos, imgWidth, imgHeight);
      }

      const xRight = pageWidth - margin - imgWidth;
      if (base64Logo) {
        doc.addImage(base64Logo, "PNG", xRight, yPos, imgWidth, imgHeight);
      }

      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);

      const centerX = pageWidth / 2;
      const lineSpacing = 5;

      const line1 = companyInfo?.name?.toUpperCase() || "NOME DA EMPRESA";
      const line2 = companyInfo?.locais?.[0]?.endereco || "";
      const line3 = companyInfo?.locais?.[0]?.telefone || "";

      doc.setFontSize(8);
      doc.text(line1, centerX, yPos + 3, { align: "center" });
      doc.text(line2, centerX, yPos + 3 + lineSpacing, { align: "center" });
      doc.text(line3, centerX, yPos + 3 + lineSpacing * 2, {
        align: "center",
      });

      let startY = yPos + lineSpacing * 3;

      const tableWidth = pageWidth - margin * 2;
      const colWidth = tableWidth / selectedColumns.length;

      const textColor = 50;

      doc.setFillColor(52, 73, 94);
      doc.rect(margin, startY, tableWidth, 8, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(9);

      doc.rect(margin, startY, tableWidth, 8, "F");
      let currentX = margin;
      selectedColumns.forEach((col) => {
        doc.text(col.label, currentX + 2, startY + 5);
        currentX += colWidth;
      });

      doc.setTextColor(textColor);
      doc.setFontSize(8);
      startY += 8;

      formattedData.forEach((row: string[]) => {
        if (startY > doc.internal.pageSize.getHeight() - 20) {
          doc.addPage();
          startY = 20;
        }
        currentX = margin;
        row.forEach((cellText) => {
          doc.text(cellText, currentX + 2, startY + 5);
          currentX += colWidth;
        });
        startY += 7;
      });

      const blob = doc.output("bloburl");
      const iframe = document.createElement("iframe");

      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "none";

      iframe.src = blob as unknown as string;
      document.body.appendChild(iframe);

      iframe.onload = () => {
        if (iframe.contentWindow) {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        }

        window.addEventListener(
          "focus",
          () => {
            setTimeout(() => {
              if (document.body.contains(iframe)) {
                document.body.removeChild(iframe);
                URL.revokeObjectURL(iframe.src);
              }
            }, 1000);
          },
          { once: true },
        );
      };
    } catch (error) {
      console.error("Erro na impressão:", error);
    }
  };

  return (
    <>
      <Modal
        show={showModal}
        onHide={handleClose}
        centered={true}
        contentClassName="me-1 pt-0 mt-0"
        size="xl"
      >
        <Modal.Header
          className="d-flex align-items-center pt-2 mt-0 pb-2 mb-0"
          closeButton
        >
          <Modal.Title>{modalTitle}</Modal.Title>
        </Modal.Header>

        <Modal.Body className="pt-2 mt-0">
          <div className="card-body py-1 border-bottom pt-0 mt-0">
            <div className="d-flex flex-wrap gap-3 pt-0 mt-0 pb-0 mb-0 py-2">
              {selectedCategories.map((cat) => (
                <div
                  className="form-check form-check-custom form-check-solid"
                  key={cat.id}
                >
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={cat.defaultChecked}
                    onChange={(e) =>
                      handleCategoryChange(cat.id, e.target.checked)
                    }
                    id={`checkbox_${cat.id}`}
                  />
                  <label
                    className="form-check-label fw-semibold text-gray-700"
                    htmlFor={`checkbox_${cat.id}`}
                  >
                    {cat.label}
                  </label>
                </div>
              ))}
              <div className="d-flex flex-column me-0"></div>
              <div className="d-flex flex-column m-0 p-0">
                <button
                  onClick={() => setOpenCollapse(!openCollapse)}
                  aria-controls="column-collapse-text"
                  aria-expanded={openCollapse}
                  className="btn btn-sm btn-secondary fw-bolder p-2 w-100 text-center"
                >
                  &nbsp;
                  {!openCollapse ? (
                    <KTSVG
                      path={`/media/icons/duotune/arrows/arr065.svg`}
                      className="svg-icon-3"
                    />
                  ) : (
                    <KTSVG
                      path={`/media/icons/duotune/arrows/arr066.svg`}
                      className="svg-icon-3 color-primary"
                    />
                  )}
                </button>
              </div>
            </div>
          </div>

          <div className="card-body py-1 border-bottom">
            <Collapse in={openCollapse}>
              <div id="column-collapse-text" className="pt-3 pb-2 border-top">
                <div className="d-flex flex-wrap gap-4 align-items-end mb-4 w-100">
                  <div className="d-flex flex-column w-200px">
                    <select
                      className="form-select form-select-sm form-select-solid"
                      value={selectedPlanId}
                      onChange={(e) => {
                        setSelectedPlanId(e.target.value);
                        if (e.target.value) setIsPersonalized(true); // Se selecionar um plano, personaliza
                      }}
                    >
                      <option key="plan-default" value="">
                        Selecione um Plano
                      </option>
                      {planOptions.map((plan) => (
                        <option key={plan.id} value={plan.id}>
                          {plan.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="d-flex flex-column w-200px">
                    <select
                      className="form-select form-select-sm form-select-solid"
                      value={selectedGender}
                      onChange={(e) => {
                        setSelectedGender(e.target.value);
                        if (e.target.value !== "all") setIsPersonalized(true);
                      }}
                    >
                      <option key="plan-default" value="">
                        Todos os Gêneros
                      </option>
                      {genderOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="d-flex gap-4">
                    <div className="d-flex align-items-center">
                      <label className="form-label fs-7 fw-bold mb-1">
                        Contratos&nbsp;(Início)
                      </label>
                      <input
                        type="date"
                        className="form-control form-control-sm form-control-solid"
                        value={contractPeriod.start}
                        onChange={(e) =>
                          handleContractPeriodChange("start", e.target.value)
                        }
                      />
                    </div>
                    <div className="d-flex align-items-center">
                      <label className="form-label fs-7 fw-bold mb-1">
                        Contratos&nbsp;(Fim)
                      </label>
                      <input
                        type="date"
                        className="form-control form-control-sm form-control-solid"
                        value={contractPeriod.end}
                        onChange={(e) =>
                          handleContractPeriodChange("end", e.target.value)
                        }
                      />
                    </div>
                  </div>
                </div>
                <div className="d-flex flex-wrap gap-4 py-2">
                  {visibleColumns.map((col) => (
                    <div
                      className="form-check form-check-custom form-check-solid"
                      key={col.id}
                    >
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={col.defaultVisible}
                        onChange={(e) =>
                          handleColumnChange(col.id, e.target.checked)
                        }
                        id={`col_checkbox_${col.id}`}
                      />
                      <label
                        className="form-check-label fw-semibold text-gray-700"
                        htmlFor={`col_checkbox_${col.id}`}
                      >
                        {col.label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </Collapse>
          </div>

          {userList ? (
            <TablesWidget3
              className="mt-5"
              data={userList}
              visibleColumns={columnsToDisplay}
            />
          ) : (
            showModal && (
              <div className="row d-flex justify-content-center align-items-center">
                <span
                  style={{ width: "50px", height: "50px" }}
                  className="spinner-border spinner-border-sm align-middle ms-2"
                ></span>
                <span className="ms-2">Nenhum registro encontrado aqui.</span>
              </div>
            )
          )}
        </Modal.Body>

        <Modal.Footer>
          <div
            className="fs-7 fw-bold text-gray-700 py-3 w-100"
            style={{ textAlign: "right" }}
          >
            <div>
              Total de{" "}
              <span className="text-primary">
                {userList?.users?.length || 0}
              </span>{" "}
              usuário(s) encontrado(s)
            </div>
          </div>
          <Button
            className="btn btn-sm"
            variant="secondary"
            onClick={handleClose}
          >
            Fechar
          </Button>

          <Button
            className="btn btn-sm btn-light-info"
            disabled={!userList}
            onClick={handlePrint}
          >
            <i className="bi bi-printer fs-4 me-1"></i>
            Imprimir
          </Button>

          <button
            type="button"
            className="btn btn-sm btn-light-danger"
            onClick={() => setShowPrintConfig(true)} // Abre a configuração antes de exportar
          >
            <i className="bi bi-file-earmark-pdf fs-4 me-1"></i>
            Exportar PDF
          </button>
          <button
            type="button"
            className="btn btn-sm btn-light-success"
            disabled={!userList}
            onClick={() => handleExport("excel")}
            /* disabled={!filtersActive} */
          >
            <i className="bi bi-file-earmark-excel fs-4 me-1"></i>
            Exportar Excel
          </button>
        </Modal.Footer>
      </Modal>
      <Modal
        show={showPrintConfig}
        onHide={() => setShowPrintConfig(false)}
        size="sm"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Configurações de Impressão</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="form-group mb-4">
            <label className="fw-bold mb-2">Orientação</label>
            <select
              className="form-select"
              value={printSettings.orientation}
              onChange={(e) =>
                setPrintSettings({
                  ...printSettings,
                  orientation: e.target.value as "p" | "l",
                })
              }
            >
              <option value="p">Retrato (Vertical)</option>
              <option value="l">Paisagem (Horizontal)</option>
            </select>
          </div>
          <div className="form-group">
            <label className="fw-bold mb-2">Tamanho do Papel</label>
            <select
              className="form-select"
              value={printSettings.pageSize}
              onChange={(e) =>
                setPrintSettings({ ...printSettings, pageSize: e.target.value })
              }
            >
              <option value="a4">A4</option>
              <option value="letter">Carta</option>
              <option value="legal">Ofício</option>
            </select>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            className="btn btn-sm btn-secondary"
            onClick={() => setShowPrintConfig(false)}
          >
            Cancelar
          </Button>
          <Button
            className="btn btn-sm btn-primary"
            onClick={() => handleExport("pdf")}
          >
            Gerar PDF
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default UserListByCategoryModal;
